import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../helpers/api';
import { deleteEstablishment, getFilteredEstablishments } from '../helpers/establishments';
import { getCategories } from '../helpers/categories';

const EstablishmentList = () => {
  const [establishments, setEstablishments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: 10
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchEstablishments();
  }, [pagination.currentPage, searchTerm, selectedCategories]);

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      setCategories(response);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchEstablishments = async () => {
    try {
      setIsLoading(true);

      // Create filters object based on your current state
      const filters = {
        search: searchTerm,
        category: selectedCategories.length === 1 ? selectedCategories[0] : undefined,
        sort: 'id:desc'
      };

      // Call getFilteredEstablishments with current pagination
      const { data, pagination: paginationData } = await getFilteredEstablishments(
        filters,
        pagination.currentPage,
        pagination.pageSize
      );

      setEstablishments(data);
      setPagination(prev => ({
        ...prev,
        totalPages: Math.ceil(paginationData.total / pagination.pageSize)
      }));
    } catch (err) {
      setError('Erreur lors du chargement des spots');
      console.error('Error fetching establishments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce spot ?')) {
      return;
    }

    try {
      const response = await deleteEstablishment(id);
      if (response) {
        setEstablishments(prev => prev.filter(est => est.documentId !== id));
        // Afficher message de succès
        const successMessage = document.getElementById('successMessage');
        successMessage.innerText = 'Spot supprimé avec succès';
        successMessage.style.display = 'block';
        setTimeout(() => {
          successMessage.style.display = 'none';
        }, 3000);
      } else {
        setError('Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur lors de la suppression');
      console.error('Error deleting establishment:', err);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  if (isLoading && establishments.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Gestion des spots</h2>
        <Link to="/establishments/new" className="btn btn-primary">
          <i className="bi bi-plus-lg me-2"></i>
          Ajouter un spot
        </Link>
      </div>

      <div className="alert alert-success" id="successMessage" style={{ display: 'none' }}></div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row mb-4">
        <div className="col-md-8">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="search"
              className="form-control"
              placeholder="Rechercher un spot..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="dropdown">
            <button
              className="btn btn-outline-secondary dropdown-toggle w-100"
              type="button"
              data-bs-toggle="dropdown"
            >
              Filtrer par catégorie
            </button>
            <div className="dropdown-menu p-3" style={{ width: '250px' }}>
              {categories.map(category => (
                <div key={category.id} className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`category-${category.id}`}
                    checked={selectedCategories.includes(category.id)}
                    onChange={() => handleCategoryToggle(category.id)}
                  />
                  <label className="form-check-label" htmlFor={`category-${category.id}`}>
                    {category.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="bg-light">
              <tr>
                <th>Nom</th>
                <th>Catégorie</th>
                <th>Ville</th>
                <th>Téléphone</th>
                <th>Status</th>
                <th width="150">Actions</th>
              </tr>
            </thead>
            <tbody>
              {establishments.map(establishment => (
                <tr key={establishment.id}>
                  <td className="align-middle">
                    <div className="d-flex align-items-center">
                      {establishment.images && (
                        <img
                          src={API_BASE_URL + establishment.images[0]?.url || '/placeholder.jpg'}
                          alt=""
                          className="rounded me-2"
                          style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                        />
                      )}
                      <div>
                        <div className="fw-semibold">{establishment.name}</div>
                        <small className="text-muted">{establishment.email}</small>
                      </div>
                    </div>
                  </td>
                  <td className="align-middle">{establishment.category?.name}</td>
                  <td className="align-middle">{establishment.city}</td>
                  <td className="align-middle">{establishment.phone}</td>
                  <td className="align-middle">
                    <span className={`badge ${establishment.is_verified ? 'bg-success' : 'bg-warning'}`}>
                      {establishment.is_verified ? 'Vérifié' : 'En attente'}
                    </span>
                  </td>
                  <td className="align-middle">
                    <div className="btn-group">
                      <Link
                        to={`/establishments/${establishment.documentId}`}
                        className="btn btn-sm btn-outline-primary"
                        title="Éditer"
                      >
                        <i className="bi bi-pencil"></i>
                      </Link>
                      <button
                        onClick={() => handleDelete(establishment.documentId)}
                        className="btn btn-sm btn-outline-danger"
                        title="Supprimer"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
            </li>

            {[...Array(pagination.totalPages)].map((_, index) => (
              <li
                key={index + 1}
                className={`page-item ${pagination.currentPage === index + 1 ? 'active' : ''}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </button>
              </li>
            ))}

            <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default EstablishmentList;